// banking/src/pages/onboarding/corporate/components/NAICSSelector.tsx

import React, { useState, useEffect, useRef } from 'react';
import { getTranslation, type Language } from '../corporate-translations';

interface NAICSSelectorProps {
  value: string;
  onChange: (code: string) => void;
  language: Language;
  error?: string;
  label?: string;
  required?: boolean;
}

interface NAICSCode {
  code: string;
  title: string;
}

const NAICSSelector: React.FC<NAICSSelectorProps> = ({
  value,
  onChange,
  language,
  error,
  label,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [naicsCodes, setNaicsCodes] = useState<NAICSCode[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load NAICS codes from backend constants
  useEffect(() => {
    const loadNAICSCodes = async () => {
      setLoading(true);
      try {
        // Import NAICS codes directly (we'll need to expose them)
        // For now, use a subset of common codes
        const commonCodes: NAICSCode[] = [
          { code: '541511', title: 'Custom Computer Programming Services' },
          { code: '541512', title: 'Computer Systems Design Services' },
          { code: '541519', title: 'Other Computer Related Services' },
          { code: '541611', title: 'Administrative Management and General Management Consulting Services' },
          { code: '541618', title: 'Other Management Consulting Services' },
          { code: '541990', title: 'All Other Professional, Scientific, and Technical Services' },
          { code: '522110', title: 'Commercial Banking' },
          { code: '522292', title: 'Real Estate Credit' },
          { code: '523110', title: 'Investment Banking and Securities Dealing' },
          { code: '523920', title: 'Portfolio Management' },
          { code: '524210', title: 'Insurance Agencies and Brokerages' },
          { code: '531210', title: 'Offices of Real Estate Agents and Brokers' },
          { code: '531390', title: 'Other Activities Related to Real Estate' },
          { code: '236220', title: 'Commercial and Institutional Building Construction' },
          { code: '238210', title: 'Electrical Contractors and Other Wiring Installation Contractors' },
          { code: '238220', title: 'Plumbing, Heating, and Air-Conditioning Contractors' },
          { code: '423430', title: 'Computer and Computer Peripheral Equipment and Software Merchant Wholesalers' },
          { code: '423850', title: 'Service Establishment Equipment and Supplies Merchant Wholesalers' },
          { code: '424990', title: 'Other Miscellaneous Nondurable Goods Merchant Wholesalers' },
          { code: '441110', title: 'New Car Dealers' },
          { code: '445110', title: 'Supermarkets and Other Grocery (except Convenience) Stores' },
          { code: '452311', title: 'Warehouse Clubs and Supercenters' },
          { code: '454111', title: 'Electronic Shopping' },
          { code: '454390', title: 'Other Direct Selling Establishments' },
          { code: '481111', title: 'Scheduled Passenger Air Transportation' },
          { code: '484110', title: 'General Freight Trucking, Local' },
          { code: '484121', title: 'General Freight Trucking, Long-Distance, Truckload' },
          { code: '493110', title: 'General Warehousing and Storage' },
          { code: '511210', title: 'Software Publishers' },
          { code: '518210', title: 'Data Processing, Hosting, and Related Services' },
          { code: '519130', title: 'Internet Publishing and Broadcasting and Web Search Portals' },
          { code: '541211', title: 'Offices of Certified Public Accountants' },
          { code: '541213', title: 'Tax Preparation Services' },
          { code: '541214', title: 'Payroll Services' },
          { code: '541310', title: 'Architectural Services' },
          { code: '541330', title: 'Engineering Services' },
          { code: '541380', title: 'Testing Laboratories' },
          { code: '541430', title: 'Graphic Design Services' },
          { code: '541810', title: 'Advertising Agencies' },
          { code: '541910', title: 'Marketing Research and Public Opinion Polling' },
          { code: '561110', title: 'Office Administrative Services' },
          { code: '561311', title: 'Employment Placement Agencies' },
          { code: '561320', title: 'Temporary Help Services' },
          { code: '561499', title: 'All Other Business Support Services' },
          { code: '561621', title: 'Security Systems Services (except Locksmiths)' },
          { code: '562111', title: 'Solid Waste Collection' },
          { code: '611519', title: 'Other Technical and Trade Schools' },
          { code: '611691', title: 'Exam Preparation and Tutoring' },
          { code: '621111', title: 'Offices of Physicians (except Mental Health Specialists)' },
          { code: '621210', title: 'Offices of Dentists' },
          { code: '621330', title: 'Offices of Mental Health Practitioners (except Physicians)' },
          { code: '622110', title: 'General Medical and Surgical Hospitals' },
          { code: '623110', title: 'Nursing Care Facilities (Skilled Nursing Facilities)' },
          { code: '624410', title: 'Child Day Care Services' },
          { code: '711510', title: 'Independent Artists, Writers, and Performers' },
          { code: '713120', title: 'Amusement Arcades' },
          { code: '713950', title: 'Bowling Centers' },
          { code: '721110', title: 'Hotels (except Casino Hotels) and Motels' },
          { code: '722110', title: 'Full-Service Restaurants' },
          { code: '722211', title: 'Limited-Service Restaurants' },
          { code: '722320', title: 'Caterers' },
          { code: '722511', title: 'Full-Service Restaurants' },
          { code: '812111', title: 'Barber Shops' },
          { code: '812112', title: 'Beauty Salons' },
          { code: '812210', title: 'Funeral Homes and Funeral Services' },
          { code: '812310', title: 'Coin-Operated Laundries and Drycleaners' },
          { code: '812320', title: 'Drycleaning and Laundry Services (except Coin-Operated)' },
          { code: '812910', title: 'Pet Care (except Veterinary) Services' },
          { code: '812990', title: 'All Other Personal Services' },
          { code: '813110', title: 'Religious Organizations' },
          { code: '813211', title: 'Grantmaking Foundations' },
          { code: '813312', title: 'Environment, Conservation and Wildlife Organizations' },
          { code: '814110', title: 'Private Households' },
          { code: '921110', title: 'Executive Offices' },
          { code: '921190', title: 'Other General Government Support' },
          { code: '922120', title: 'Police Protection' },
          { code: '923110', title: 'Administration of Education Programs' },
          { code: '924110', title: 'Administration of Air and Water Resource and Solid Waste Management Programs' },
          { code: '925110', title: 'Administration of Housing Programs' },
          { code: '926110', title: 'Administration of General Economic Programs' },
        ];

        setNaicsCodes(commonCodes);
      } catch (error) {
        console.error('Error loading NAICS codes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNAICSCodes();
  }, []);

  const filteredCodes = naicsCodes.filter((item) =>
    item.code.includes(searchTerm) ||
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCode = naicsCodes.find((c) => c.code === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`w-full px-4 py-2 text-left border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {selectedCode ? (
          <span>{selectedCode.code} - {selectedCode.title}</span>
        ) : (
          <span className="text-gray-400">
            {loading ? getTranslation('loading', language) : getTranslation('business_sector_placeholder', language)}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={getTranslation('search_placeholder', language)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredCodes.length > 0 ? (
              filteredCodes.map((code) => (
                <button
                  key={code.code}
                  type="button"
                  onClick={() => {
                    onChange(code.code);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-[#F5F3E8] transition-colors ${
                    code.code === value ? 'bg-blue-100 font-medium' : ''
                  }`}
                >
                  <div className="font-medium text-sm text-[#B3A369]">{code.code}</div>
                  <div className="text-sm text-gray-700">{code.title}</div>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                {getTranslation('no_results', language)}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default NAICSSelector;
